import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface OnlineStatus {
  isOnline: boolean;
  lastSeenAt: string | null;
}

export function useOnlineStatus(userId?: string): OnlineStatus {
  const [status, setStatus] = useState<OnlineStatus>({
    isOnline: false,
    lastSeenAt: null,
  });

  useEffect(() => {
    if (!userId) return;

    // Fetch initial status
    const fetchStatus = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("is_online, last_seen_at")
        .eq("id", userId)
        .maybeSingle();

      if (data) {
        setStatus({
          isOnline: data.is_online || false,
          lastSeenAt: data.last_seen_at,
        });
      }
    };

    fetchStatus();

    // Subscribe to profile changes
    const channel = supabase
      .channel(`online-status:${userId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "profiles",
          filter: `id=eq.${userId}`,
        },
        (payload) => {
          const updated = payload.new as { is_online?: boolean; last_seen_at?: string };
          setStatus({
            isOnline: updated.is_online || false,
            lastSeenAt: updated.last_seen_at || null,
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  return status;
}

// Hook to track current user's online presence
export function usePresenceTracker() {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    // Mark user as online
    const setOnline = async () => {
      await supabase
        .from("profiles")
        .update({ 
          is_online: true, 
          last_seen_at: new Date().toISOString() 
        })
        .eq("id", user.id);
    };

    // Mark user as offline
    const setOffline = async () => {
      await supabase
        .from("profiles")
        .update({ 
          is_online: false, 
          last_seen_at: new Date().toISOString() 
        })
        .eq("id", user.id);
    };

    setOnline();

    // Update last_seen periodically
    const interval = setInterval(() => {
      supabase
        .from("profiles")
        .update({ last_seen_at: new Date().toISOString() })
        .eq("id", user.id);
    }, 60000); // Every minute

    // Handle visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setOnline();
      } else {
        setOffline();
      }
    };

    // Handle beforeunload - use synchronous XHR as fallback since sendBeacon doesn't support auth headers
    const handleBeforeUnload = () => {
      // sendBeacon doesn't support custom headers, use sync XHR for offline status
      try {
        const xhr = new XMLHttpRequest();
        xhr.open('PATCH', `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/profiles?id=eq.${user.id}`, false);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('apikey', import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY);
        xhr.setRequestHeader('Authorization', `Bearer ${supabase.auth.getSession().then(s => s.data.session?.access_token) || ''}`);
        xhr.setRequestHeader('Prefer', 'return=minimal');
        xhr.send(JSON.stringify({ is_online: false, last_seen_at: new Date().toISOString() }));
      } catch (e) {
        // Silent fail - best effort on page unload
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      setOffline();
    };
  }, [user]);
}
